<template>
  <div class="message-list">
    <div
      class="message"
      v-for="message in messages.list"
      :key="message.id"
      :class="{ user: message.sender === user.id, staff: message.sender !== user.id }"
    >
      <div class="message__header">
        <div class="message__header-start">
          <h5 class="message-sender">
            {{ senderName(message) }}
            <span v-if="$props.staffView && message.status === Message.options.MESSAGE_SENT" class="message-type">
              {{ messageTypeName[message.messageType] }}
            </span>
            <span v-else-if="$props.staffview && message.status === Message.options.MESSAGE_SCHEDULED" class="message-type">
              {{ message.status }}
              scheduled a message
            </span>
          </h5>
        </div>
        <div class="message-timestamp">
          <span v-if="message.status === Message.options.MESSAGE_SENT">{{ message.datetimeCreated | formatDateLong($language.current) }}</span>
          <template v-else>
            <span>
              <font-awesome-icon
                class="calendar"
                :icon="['fa', 'clock']"
                :style="{ fontSize: '16px' }"
              ></font-awesome-icon>
              {{ message.sendDatetime | formatDateLong($language.current) }}
              <font-awesome-icon
                class="trash"
                :icon="['fa', 'trash']"
                :style="{ fontSize: '16px', color: 'red', cursor: 'pointer' }"
                @click="deleteScheduledMessage(message.id)"
              ></font-awesome-icon>
            </span>
          </template>
        </div>
      </div>
      <div class="message-text-flex">
        <div
          v-if="
            [
              Message.options.DIGITAL_MESSAGE,
              Message.options.DIGITAL_MESSAGE_ENGLISH,
              Message.options.DIGITAL_MESSAGE_SPANISH,
            ].includes(message.messageType)
          "
          class="message-text"
          :class="{ 'scheduled': message.status === Message.options.MESSAGE_SCHEDULED }"
          v-html="message.text"
        ></div>
      </div>
      <div
        v-if="
          ![
            Message.options.DIGITAL_MESSAGE,
            Message.options.DIGITAL_MESSAGE_ENGLISH,
            Message.options.DIGITAL_MESSAGE_SPANISH,
          ].includes(message.messageType)
        "
        class="message-note"
      >
        {{ message.note }}
      </div>
    </div>

    <LoadSpinner
      v-if="messages.loadingNextPage"
      :color="`#${organizationDetails.darkBrandColor}`"
    />

    <md-button
      class="md-raised"
      v-if="messages.pagination.hasNextPage"
      @click="
        () =>
          messages.addNextPage().then(() => {
            this.$emit('nextPageLoaded')
          })
      "
      :disabled="messages.loadingNextPage"
    >
      Load Older Messages
    </md-button>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { LoadSpinner } from '@/components'
import Message from '@/services/messages'

/**
 * Display a list of messages
 *
 * @prop {CollectionManager} messages - The collection of messages to display.
 */
export default {
  name: 'MessagesList',
  data() {
    return {
      Message,
      messageTypeName: {
        [Message.options.DIGITAL_MESSAGE]: 'sent a message',
        [Message.options.DIGITAL_MESSAGE_ENGLISH]: 'sent a message',
        [Message.options.DIGITAL_MESSAGE_SPANISH]: this.$gettext('sent a message'),
        [Message.options.PHONE_CALL]: 'called',
        [Message.options.BESPOKE_SMS]: 'sent a bespoke SMS',
        [Message.options.PHONE_CALL_INCOMING]: 'received a call',
        [Message.options.PHONE_CALL_OUTGOING]: 'called',
        [Message.options.BESPOKE_SMS_INCOMING]: 'received a bespoke SMS',
        [Message.options.BESPOKE_SMS_OUTGOING]: 'sent a bespoke SMS',
      },
    }
  },
  components: {
    LoadSpinner,
  },
  props: {
    messages: {
      type: Object,
      required: true,
    },
    staffView: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    senderName(message) {
      return this.user && message.sender === this.user.id
        ? this.user.fullName
        : message.senderRef.firstName || this.$gettext('Unnamed User')
    },
    async deleteScheduledMessage(id) {
      await Message.api.delete(id)
      this.$emit('message-deleted')
    }
  },
  computed: {
    ...mapState(['organizationDetails', 'user']),
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';

.message {
  padding: 1rem 0;
  border-top: 1px solid $soft-gray;

  &__header {
    display: flex;
    margin-bottom: 0.5rem;
  }

  /* User message styles */
  &.user {
    .message__header,
    .message-text-flex {
      justify-content: flex-end;
    }

    .message-text-flex {
      margin-right: 1.5rem;
    }

    .message-text {
      background-color: rgba(0, 103, 185, 0.1);
      border-radius: 1.5rem 0 1.5rem 1.5rem;

      &.scheduled {
        background-color: #ececec;
      }
    }
  }

  /* Staff message styles */
  &.staff {
    .message__header,
    .message-text-flex {
      justify-content: flex-start;
    }

    .message-text-flex {
      margin-left: 1.5rem;
    }

    .message-text {
      background-color: #f4f4f5;
      border-radius: 0 1.5rem 1.5rem 1.5rem;

      &.scheduled {
        background-color: #ececec;
      }
    }
  }

  &__header-start {
  }

  &__header-end {
  }

  .message-sender {
    font-size: 1rem;
    font-weight: bold;
    margin: 0;
    margin-right: 1rem;
  }

  .message-type {
    color: $blue;
    font-weight: normal;
    font-style: italic;
  }

  .message-timestamp {
    color: #637381;

    .trash {
      margin-left: 0.5rem;
    }
  }

  .message-text-flex {
    display: flex;
    flex-direction: row;
  }

  .message-text,
  .message-note {
    white-space: pre-line;
    padding: 1rem 1.5rem;
    width: 70%;
  }

  .message-note {
    width: 100%;
  }
}
</style>
